import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Button,
  Row,
  Col,
  InputGroup,
  Tabs,
  Tab,
  ProgressBar,
  Badge,
} from 'react-bootstrap';
import {
  Tarefa,
  TarefaInsertFormData,
  TarefaUpdateFormData,
  TarefaStatus,
  TarefaLink,
} from '../../types/tarefa';
import { User } from 'types/user';
import { Projeto, ProjetoMinDTO } from 'types/projeto';
import { ExternoDTO } from 'types/externo';
import { searchProjetosAPI, getProjetoDetailsAPI } from '../../api/requestsApi';
import { getAllExternosAPI } from '../../api/externoApi';
import { extendProjetoPrazo } from '../../services/projetoService';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { useAuth } from '../../AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Permission } from '../../permissions/rolePermissions';
import { useSubtarefas } from '../../hooks/useSubtarefas';
import { dividirSubtarefas } from '../../services/subtarefaService';
import { getAllUsers } from '../../services/userService';
import { TAREFA_STATUS_LABELS } from '../../constants/tarefaStatus';
import { FREQUENCIA_RECORRENCIA_LABELS } from '../../constants/frequenciaRecorrencia';
import { useRequisicaoConflitos } from '../../hooks/useRequisicaoConflitos';
import { validarRequisicoes } from '../../services/equipamentoService';
import TarefaRequisicoesTab from './TarefaRequisicoesTab';
import TarefaFaseSelect from './TarefaFaseSelect';

interface TarefaModalProps {
  show: boolean;
  onHide: () => void;
  tarefa?: Tarefa | null;
  onSave: (
    formData: TarefaInsertFormData | TarefaUpdateFormData
  ) => void | Promise<Tarefa | void>;
  isEditing: boolean;
  onStatusChange?: (tarefaId: number, newStatus: TarefaStatus) => void;
  onArchive?: (tarefaId: number) => void | Promise<void>;
  // Lets the parent screen refresh any already-loaded task list/board data
  // that embeds this project's deadline (e.g. other Kanban cards, other
  // table rows) — this modal only updates its own local `selectedProject`.
  onProjectDeadlineExtended?: (projetoId: number) => void;
}

// Helper function to calculate working days between two dates
const calculateWorkingDays = (
  startDateStr: string,
  endDateStr: string
): number => {
  if (!startDateStr || !endDateStr) return 0;

  const startDate = new Date(startDateStr);
  const endDate = new Date(endDateStr);

  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return 0;

  let workingDays = 0;
  let currentDate = new Date(startDate);

  // Set both dates to midnight to ensure we're only comparing dates, not times
  currentDate.setHours(0, 0, 0, 0);
  const endDateMidnight = new Date(endDate);
  endDateMidnight.setHours(0, 0, 0, 0);

  // Count working days
  while (currentDate <= endDateMidnight) {
    const day = currentDate.getDay();
    if (day !== 0 && day !== 6) {
      // 0 is Sunday, 6 is Saturday
      workingDays++;
    }
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return workingDays;
};

const TarefaModal: React.FC<TarefaModalProps> = ({
  show,
  onHide,
  tarefa,
  onSave,
  isEditing,
  onStatusChange,
  onArchive,
  onProjectDeadlineExtended,
}) => {
  const { user } = useAuth();
  const { hasPermission, hasAnyPermission, isAdmin, isManager } =
    usePermissions();
  const {
    subtarefas,
    isDividida,
    totalPercentual,
    dividir,
    atualizar,
    concluir,
    reabrir,
    desfazer,
  } = useSubtarefas(isEditing ? tarefa?.id : undefined);
  const [editingSubtarefaId, setEditingSubtarefaId] = useState<number | null>(
    null,
  );
  const [editingDescricao, setEditingDescricao] = useState('');
  const [formData, setFormData] = useState<
    TarefaInsertFormData | TarefaUpdateFormData
  >({
    descricao: '',
    prioridade: '',
    prazoEstimado: '',
    prazoReal: '',
    status: 'BACKLOG',
    projetoId: 0,
    userIds: [],
    externoIds: [],
    recorrente: false,
    frequenciaRecorrencia: undefined,
    dataFimRecorrencia: '',
    links: [],
    requisicoes: [],
    faseId: null,
  });
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // Project search state
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [projectSearchResults, setProjectSearchResults] = useState<
    Array<{ id: number; designacao: string }>
  >([]);
  const [isSearchingProjects, setIsSearchingProjects] = useState(false);
  const [selectedProjectName, setSelectedProjectName] = useState('');
  // Working days state
  const [workingDays, setWorkingDays] = useState<number>(0);
  const [externos, setExternos] = useState<ExternoDTO[]>([]);
  const [activeTab, setActiveTab] = useState('colaboradores');
  // Add state for selected project details including deadline
  const [selectedProject, setSelectedProject] = useState<Projeto | null>(null);
  // Add state for deadline validation
  const [isDeadlineValid, setIsDeadlineValid] = useState(true);
  const [deadlineErrorMessage, setDeadlineErrorMessage] = useState('');
  const [extendPrazoValue, setExtendPrazoValue] = useState('');
  const [isExtendingDeadline, setIsExtendingDeadline] = useState(false);
  const [subtarefaDescricoes, setSubtarefaDescricoes] = useState<
    Record<number, string>
  >({});
  const [divideOnCreate, setDivideOnCreate] = useState(false);
  // A finished tarefa no longer holds its material, so there's nothing to warn about.
  const requisicaoConflitos = useRequisicaoConflitos(
    formData.requisicoes,
    isEditing ? tarefa?.id : undefined,
    show && formData.status !== 'DONE',
  );
  const totalConflitos = Object.values(requisicaoConflitos).filter(
    (conflitos) => conflitos.length > 0,
  ).length;

  // Fetch users and externos when modal opens
  useEffect(() => {
    if (!show) return;
    setIsLoading(true);
    Promise.all([getAllUsers(), getAllExternosAPI()])
      .then(([usersData, externosData]) => {
        setUsers(usersData);
        setExternos(externosData);
      })
      .catch(() => toast.error('Erro ao carregar dados'))
      .finally(() => setIsLoading(false));
  }, [show]);

  // Fetch project details when a project is selected
  useEffect(() => {
    if (formData.projetoId && formData.projetoId > 0) {
      getProjetoDetailsAPI(formData.projetoId)
        .then((project) => {
          setSelectedProject(project);
          // Validate deadline when project changes
          validateDeadline(formData.prazoReal, project);
        })
        .catch((error) => {
          console.error('Error fetching project details:', error);
          setSelectedProject(null);
        });
    } else {
      setSelectedProject(null);
      setIsDeadlineValid(true);
      setDeadlineErrorMessage('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.projetoId]);

  // Set form data when editing
  useEffect(() => {
    setSubtarefaDescricoes({});
    setDivideOnCreate(false);
    if (isEditing && tarefa) {
      setFormData({
        id: tarefa.id,
        descricao: tarefa.descricao,
        prioridade: tarefa.prioridade,
        prazoEstimado: tarefa.prazoEstimado
          ? tarefa.prazoEstimado.split('T')[0]
          : '',
        prazoReal: tarefa.prazoReal ? tarefa.prazoReal.split('T')[0] : '',
        status: tarefa.status,
        projetoId: tarefa.projeto.id,
        userIds: tarefa.users.map((user) => user.id),
        externoIds: tarefa.externos?.map((externo) => externo.id) || [],
        version: tarefa.version,
        recorrente: tarefa.recorrente ?? false,
        frequenciaRecorrencia: tarefa.frequenciaRecorrencia ?? undefined,
        dataFimRecorrencia: tarefa.dataFimRecorrencia
          ? tarefa.dataFimRecorrencia.split('T')[0]
          : '',
        links: tarefa.links ?? [],
        // Left undefined if this screen's tarefa came without the field: the
        // backend then keeps the saved requisições instead of clearing them.
        requisicoes: tarefa.requisicoes,
        // Same idea: undefined (screen didn't load it) keeps the saved phase.
        faseId: tarefa.faseId,
      });
      setSelectedProjectName(tarefa.projeto.designacao);
      setWorkingDays(
        tarefa.workingDays ??
          calculateWorkingDays(
            tarefa.prazoEstimado?.split('T')[0] || '',
            tarefa.prazoReal?.split('T')[0] || ''
          )
      );

      // The setFormData call above changes formData.projetoId, which
      // triggers the "Fetch project details when a project is selected"
      // effect on its own — fetching it again here duplicated the request
      // on every edit-open.
    } else {
      setFormData({
        descricao: '',
        prioridade: '',
        prazoEstimado: '',
        prazoReal: '',
        status: 'BACKLOG',
        projetoId: 0,
        userIds: [],
        externoIds: [],
        recorrente: false,
        frequenciaRecorrencia: undefined,
        dataFimRecorrencia: '',
        links: [],
        requisicoes: [],
        faseId: null,
      });
      setSelectedProjectName('');
      setWorkingDays(0);
      setSelectedProject(null);
      setIsDeadlineValid(true);
      setDeadlineErrorMessage('');
    }
  }, [isEditing, tarefa, show]);

  // Calculate working days when dates change
  useEffect(() => {
    if (formData.prazoEstimado && formData.prazoReal) {
      setWorkingDays(
        calculateWorkingDays(formData.prazoEstimado, formData.prazoReal)
      );
    } else {
      setWorkingDays(0);
    }
  }, [formData.prazoEstimado, formData.prazoReal]);

  // Validate deadline when task deadline changes
  useEffect(() => {
    if (selectedProject && formData.prazoReal) {
      validateDeadline(formData.prazoReal, selectedProject);
    }
  }, [formData.prazoReal, selectedProject]);

  // Function to validate task deadline against project deadline
  const validateDeadline = (taskDeadlineStr?: string, project?: Projeto) => {
    if (!taskDeadlineStr || !project || !project.prazo) {
      setIsDeadlineValid(true);
      setDeadlineErrorMessage('');
      return true;
    }

    const taskDeadline = new Date(taskDeadlineStr);
    const projectDeadline = new Date(project.prazo.split('T')[0]);

    // Set time to midnight to compare only dates
    taskDeadline.setHours(0, 0, 0, 0);
    projectDeadline.setHours(0, 0, 0, 0);

    const isValid = taskDeadline <= projectDeadline;
    setIsDeadlineValid(isValid);

    if (!isValid) {
      setDeadlineErrorMessage(
        `O prazo da tarefa (${format(
          taskDeadline,
          'dd/MM/yyyy'
        )}) não pode exceder o prazo final do projeto (${format(
          projectDeadline,
          'dd/MM/yyyy'
        )})`
      );
      setExtendPrazoValue(taskDeadlineStr);
    } else {
      setDeadlineErrorMessage('');
    }

    return isValid;
  };

  // Extend the project's deadline directly from the task modal so the task's
  // deadline can be accepted without having to leave and reopen this form.
  const handleExtendProjectDeadline = async () => {
    if (!selectedProject || !extendPrazoValue) return;

    setIsExtendingDeadline(true);
    try {
      const updatedProject = await extendProjetoPrazo(
        selectedProject.id,
        extendPrazoValue
      );
      setSelectedProject(updatedProject);
      validateDeadline(formData.prazoReal, updatedProject);
      onProjectDeadlineExtended?.(updatedProject.id);
      toast.success(
        `Prazo do projeto atualizado para ${format(
          new Date(extendPrazoValue),
          'dd/MM/yyyy'
        )}`
      );
    } catch (error) {
      console.error('Error extending projeto prazo:', error);
      toast.error('Não foi possível estender o prazo do projeto');
    } finally {
      setIsExtendingDeadline(false);
    }
  };

  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = event.target;
    if (name === 'status' && isEditing && tarefa) {
      // onStatusChange triggers the real backend status update, which already
      // creates and broadcasts the TAREFA_STATUS_ALTERADO notification server-side —
      // sending another one from here duplicated it.
      onStatusChange?.(tarefa.id, value as TarefaStatus);
    }

    // For deadline changes, validate immediately
    if (name === 'prazoReal' && selectedProject) {
      const isValid = validateDeadline(value, selectedProject);
      if (!isValid) {
        // Optionally show toast warning immediately
        // toast.warning(deadlineErrorMessage);
      }
    }

    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  // Handle project search
  const handleProjectSearch = async () => {
    if (!projectSearchQuery.trim()) {
      setProjectSearchResults([]);
      return;
    }
    setIsSearchingProjects(true);
    try {
      const response = await searchProjetosAPI(projectSearchQuery);
      if (response && response.content && response.content.length > 0) {
        setProjectSearchResults(response.content);
      } else {
        setProjectSearchResults([]);
        toast.info('Nenhum projeto encontrado com esse termo de busca');
      }
    } catch (error) {
      console.error('Error searching projects:', error);
      toast.error('Erro ao pesquisar projetos');
      setProjectSearchResults([]);
    } finally {
      setIsSearchingProjects(false);
    }
  };

  // Handle project selection
  const handleSelectProject = (projeto: ProjetoMinDTO) => {
    setFormData((prevData) => ({
      ...prevData,
      projetoId: projeto.id,
      // A phase belongs to one projeto: switching projeto clears it (explicit null,
      // so the backend removes it rather than keeping it).
      faseId: projeto.id === prevData.projetoId ? prevData.faseId : null,
    }));
    setSelectedProjectName(projeto.designacao);
    setProjectSearchResults([]);
    setProjectSearchQuery('');

    // Fetch complete project details
    getProjetoDetailsAPI(projeto.id)
      .then((project: Projeto) => {
        setSelectedProject(project);
        // Validate current deadline against new project
        if (formData.prazoReal) {
          validateDeadline(formData.prazoReal, project);
        }
      })
      .catch((error) => {
        console.error('Error fetching project details:', error);
        setSelectedProject(null);
      });
  };

  const handleUserSelect = (userId: number) => {
    setFormData((prevData) => ({
      ...prevData,
      userIds: prevData.userIds.includes(userId)
        ? prevData.userIds.filter((id) => id !== userId)
        : [...prevData.userIds, userId],
    }));
  };

  // Handle shared links (e.g. OneDrive) attached to the tarefa
  const handleAddLink = () => {
    setFormData((prevData) => ({
      ...prevData,
      links: [...(prevData.links || []), { url: '', descricao: '' }],
    }));
  };

  const handleLinkChange = (
    index: number,
    field: keyof TarefaLink,
    value: string
  ) => {
    setFormData((prevData) => ({
      ...prevData,
      links: (prevData.links || []).map((link, i) =>
        i === index ? { ...link, [field]: value } : link
      ),
    }));
  };

  const handleRemoveLink = (index: number) => {
    setFormData((prevData) => ({
      ...prevData,
      links: (prevData.links || []).filter((_, i) => i !== index),
    }));
  };

  // Handle externo selection
  const handleExternoSelect = (externoId: number) => {
    setFormData((prevData) => ({
      ...prevData,
      externoIds: prevData.externoIds?.includes(externoId)
        ? prevData.externoIds.filter((id) => id !== externoId)
        : [...(prevData.externoIds || []), externoId],
    }));
  };

  const handleSave = () => {
    if (formData.projetoId === 0) {
      toast.warning('Por favor, selecione um projeto válido');
      return;
    }
    if (!formData.descricao.trim()) {
      toast.warning('Por favor, forneça uma descrição para a tarefa');
      return;
    }
    if (formData.recorrente && !formData.frequenciaRecorrencia) {
      toast.warning('Por favor, selecione a frequência da Tarefa');
      return;
    }

    // Validate deadline before saving
    if (selectedProject && formData.prazoReal) {
      const isValid = validateDeadline(formData.prazoReal, selectedProject);
      if (!isValid) {
        toast.error(deadlineErrorMessage);
        return;
      }
    }

    // Drop any link row the user left without a URL rather than rejecting the save,
    // but reject a filled-in one with an unsafe scheme (e.g. javascript:) — it's
    // rendered as a clickable link elsewhere, so only http(s) is allowed.
    const linksToSave = (formData.links || []).filter(
      (link) => link.url.trim() !== ''
    );
    const invalidLink = linksToSave.find(
      (link) => !/^https?:\/\//i.test(link.url.trim())
    );
    if (invalidLink) {
      toast.warning(
        `O link "${invalidLink.url}" deve começar por http:// ou https://`
      );
      return;
    }

    const requisicaoInvalida = validarRequisicoes(formData.requisicoes || []);
    if (requisicaoInvalida) {
      toast.warning(requisicaoInvalida);
      setActiveTab('material');
      return;
    }

    // Include workingDays in the form data if both dates are available
    const updatedFormData = {
      ...formData,
      workingDays:
        formData.prazoEstimado && formData.prazoReal ? workingDays : undefined,
      links: linksToSave,
    };

    if (isEditing && tarefa) {
      // onSave persists the edit; the backend already notifies affected users
      // (TAREFA_ATRIBUIDA/TAREFA_REMOVIDA/TAREFA_EDITADA) as part of that call.
      onSave({ ...updatedFormData, id: tarefa.id } as TarefaUpdateFormData);
    } else {
      const savePromise = onSave(updatedFormData as TarefaInsertFormData);
      if (divideOnCreate && formData.userIds.length >= 2) {
        Promise.resolve(savePromise)
          .then((created) => {
            if (!created) return undefined;
            const itens = formData.userIds.map((userId) => ({
              userId,
              descricao: subtarefaDescricoes[userId]?.trim() || undefined,
            }));
            return dividirSubtarefas(created.id, itens);
          })
          .then((result) => {
            if (result) {
              toast.success('Tarefa dividida em subtarefas.');
            }
          })
          .catch((err) => {
            const message =
              err instanceof Error
                ? err.message
                : 'Erro ao dividir a tarefa em subtarefas.';
            toast.error(message);
          });
      }
      // onSave (above) already creates the tarefa on the backend, which notifies
      // each assigned user (TAREFA_ATRIBUIDA) server-side — sending another one
      // here duplicated it, and always pointed at tarefaId 0 since this fired
      // before the created task's real id was known.
    }
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static" keyboard={false}>
      <Modal.Header closeButton>
        <Modal.Title>
          {isEditing ? 'Editar Tarefa' : 'Criar Nova Tarefa'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className="text-center py-4">Carregando dados...</div>
        ) : (
          <Form>
            <Row>
              <Col xs={12}>
                <Form.Group controlId="formDescricao" className="mb-3">
                  <Form.Label>Descrição</Form.Label>
                  <Form.Control
                    type="text"
                    name="descricao"
                    value={formData.descricao}
                    onChange={handleInputChange}
                    required
                    maxLength={255}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col xs={12} md={6}>
                <Form.Group controlId="formPrioridade" className="mb-3">
                  <Form.Label>Prioridade</Form.Label>
                  <Form.Select
                    name="prioridade"
                    value={formData.prioridade}
                    onChange={handleInputChange}
                  >
                    <option value="">Selecionar...</option>
                    <option value="Urgente">Urgente</option>
                    <option value="Alta">Alta</option>
                    <option value="Média">Média</option>
                    <option value="Baixa">Baixa</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group controlId="formStatus" className="mb-3">
                  <Form.Label>Estado</Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    {Object.entries(TAREFA_STATUS_LABELS).map(
                      ([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      )
                    )}
                  </Form.Select>
                  {isEditing && (
                    <Form.Text className="text-muted">
                      A alteração de estado é guardada de imediato — não é
                      necessário clicar em "Atualizar".
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col xs={12} md={6}>
                <Form.Group controlId="formPrazoEstimado" className="mb-3">
                  <Form.Label>Início</Form.Label>
                  <Form.Control
                    type="date"
                    name="prazoEstimado"
                    value={formData.prazoEstimado}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group controlId="formPrazoReal" className="mb-3">
                  <Form.Label>Prazo</Form.Label>
                  <Form.Control
                    type="date"
                    name="prazoReal"
                    value={formData.prazoReal}
                    onChange={handleInputChange}
                    isInvalid={!isDeadlineValid}
                  />
                  {!isDeadlineValid && (
                    <Form.Control.Feedback type="invalid">
                      {deadlineErrorMessage}
                    </Form.Control.Feedback>
                  )}
                  {selectedProject?.prazo && (
                    <Form.Text className="text-muted">
                      Prazo do projeto:{' '}
                      {format(new Date(selectedProject.prazo), 'dd/MM/yyyy')}
                    </Form.Text>
                  )}
                  {!isDeadlineValid && selectedProject && (
                    <div className="mt-2">
                      {hasAnyPermission([
                        Permission.EDIT_PROJECT,
                        Permission.EXTEND_PROJECT_DEADLINE,
                      ]) ? (
                        <InputGroup size="sm">
                          <Form.Control
                            type="date"
                            value={extendPrazoValue}
                            onChange={(e) =>
                              setExtendPrazoValue(e.target.value)
                            }
                          />
                          <Button
                            variant="outline-primary"
                            disabled={isExtendingDeadline || !extendPrazoValue}
                            onClick={handleExtendProjectDeadline}
                          >
                            {isExtendingDeadline
                              ? 'A estender...'
                              : 'Estender prazo do projeto'}
                          </Button>
                        </InputGroup>
                      ) : (
                        <Form.Text className="text-muted">
                          Peça a um gestor do projeto para alargar o prazo.
                        </Form.Text>
                      )}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
            {/* Working Days display - update the description text */}
            {formData.prazoEstimado && formData.prazoReal && (
              <Row>
                <Col xs={12}>
                  <Form.Group controlId="formWorkingDays" className="mb-3">
                    <Form.Label>Duração (dias úteis)</Form.Label>
                    <Form.Control
                      type="text"
                      value={`${workingDays} dia(s)`}
                      readOnly
                      disabled
                    />
                    <Form.Text className="text-muted">
                      Número de dias úteis entre a data de início e o prazo
                      (excluindo fins de semana).
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            )}
            {/* Recurrence */}
            <Row>
              <Col xs={12}>
                <Form.Group controlId="formRecorrente" className="mb-3">
                  {isEditing && tarefa?.tarefaOrigemId ? (
                    <Form.Text className="text-muted">
                      Gerada automaticamente a partir da tarefa recorrente #
                      {tarefa.tarefaOrigemId}
                    </Form.Text>
                  ) : (
                    <>
                      <Form.Check
                        type="checkbox"
                        label="Tarefa recorrente"
                        checked={formData.recorrente ?? false}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFormData((prevData) => ({
                            ...prevData,
                            recorrente: checked,
                            frequenciaRecorrencia: checked
                              ? prevData.frequenciaRecorrencia
                              : undefined,
                            dataFimRecorrencia: checked
                              ? prevData.dataFimRecorrencia
                              : '',
                          }));
                        }}
                      />
                      {formData.recorrente && (
                        <Row className="mt-2">
                          <Col xs={12} md={6}>
                            <Form.Label>Frequência</Form.Label>
                            <Form.Select
                              name="frequenciaRecorrencia"
                              value={formData.frequenciaRecorrencia || ''}
                              onChange={handleInputChange}
                            >
                              <option value="">Selecionar...</option>
                              {Object.entries(FREQUENCIA_RECORRENCIA_LABELS).map(
                                ([value, label]) => (
                                  <option key={value} value={value}>
                                    {label}
                                  </option>
                                )
                              )}
                            </Form.Select>
                          </Col>
                          <Col xs={12} md={6}>
                            <Form.Label>
                              Terminar recorrência em (opcional)
                            </Form.Label>
                            <Form.Control
                              type="date"
                              name="dataFimRecorrencia"
                              value={formData.dataFimRecorrencia || ''}
                              onChange={handleInputChange}
                            />
                          </Col>
                        </Row>
                      )}
                    </>
                  )}
                </Form.Group>
              </Col>
            </Row>
            {/* Project search field */}
            <Row>
              <Col xs={12}>
                <Form.Group controlId="formProjeto" className="mb-3">
                  <Form.Label>Projeto</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Pesquisar projeto..."
                      value={projectSearchQuery}
                      onChange={(e) => setProjectSearchQuery(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleProjectSearch();
                        }
                      }}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={handleProjectSearch}
                      disabled={isSearchingProjects}
                    >
                      {isSearchingProjects ? 'Buscando...' : 'Buscar'}
                    </Button>
                  </InputGroup>
                  {selectedProjectName && (
                    <div className="mt-2">
                      <strong>Projeto selecionado:</strong>{' '}
                      {selectedProjectName}
                    </div>
                  )}
                  {projectSearchResults.length > 0 && (
                    <div
                      className="mt-2 border rounded p-2"
                      style={{ maxHeight: '200px', overflowY: 'auto' }}
                    >
                      <h6>Resultados da busca:</h6>
                      <ul className="list-group">
                        {projectSearchResults.map((projeto) => (
                          <li
                            key={projeto.id}
                            className="list-group-item list-group-item-action"
                            onClick={() => handleSelectProject(projeto)}
                            style={{ cursor: 'pointer' }}
                          >
                            {projeto.designacao}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col xs={12} md={6}>
                <TarefaFaseSelect
                  projetoId={formData.projetoId}
                  value={formData.faseId}
                  onChange={(faseId) =>
                    setFormData((prevData) => ({ ...prevData, faseId }))
                  }
                />
              </Col>
            </Row>
            {/* Users and Externos Tabs */}
            <Row>
              <Col xs={12}>
                <Tabs
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab(k || 'colaboradores')}
                  className="mb-3"
                >
                  <Tab eventKey="colaboradores" title="Colaboradores">
                    <Form.Group controlId="formUsers" className="mb-3">
                      <Form.Label>Colaboradores Atribuídos</Form.Label>
                      {isEditing && isDividida && (
                        <Form.Text className="d-block text-muted mb-2">
                          Esta tarefa já foi dividida em subtarefas; não é
                          possível alterar os colaboradores.
                        </Form.Text>
                      )}
                      <div
                        className="user-checkbox-container"
                        style={{ maxHeight: '200px', overflowY: 'auto' }}
                      >
                        {Array.isArray(users) && users.length > 0 ? (
                          <Row>
                            {users
                              .filter(
                                (user) =>
                                  user.ativo !== false ||
                                  formData.userIds.includes(user.id)
                              )
                              .map((user) => (
                                <Col xs={12} md={6} key={user.id}>
                                  <Form.Check
                                    type="checkbox"
                                    label={
                                      user.ativo === false
                                        ? `${user.name} (Inativo)`
                                        : user.name
                                    }
                                    checked={formData.userIds.includes(
                                      user.id
                                    )}
                                    disabled={isEditing && isDividida}
                                    onChange={() => handleUserSelect(user.id)}
                                    className="mb-2"
                                  />
                                </Col>
                              ))}
                          </Row>
                        ) : (
                          <p>Nenhum colaborador disponível</p>
                        )}
                      </div>
                    </Form.Group>
                  </Tab>
                  <Tab eventKey="externos" title="Externos">
                    <Form.Group controlId="formExternos" className="mb-3">
                      <Form.Label>Externos Atribuídos</Form.Label>
                      <div
                        className="externo-checkbox-container"
                        style={{ maxHeight: '200px', overflowY: 'auto' }}
                      >
                        {Array.isArray(externos) && externos.length > 0 ? (
                          <Row>
                            {externos.map((externo) => (
                              <Col xs={12} md={6} key={externo.id}>
                                <Form.Check
                                  type="checkbox"
                                  label={`${
                                    externo.name
                                  } (${externo.especialidades.join(', ')})`}
                                  checked={
                                    formData.externoIds?.includes(externo.id) ||
                                    false
                                  }
                                  onChange={() =>
                                    handleExternoSelect(externo.id)
                                  }
                                  className="mb-2"
                                />
                              </Col>
                            ))}
                          </Row>
                        ) : (
                          <p>Nenhum externo disponível</p>
                        )}
                      </div>
                    </Form.Group>
                  </Tab>
                  <Tab eventKey="links" title="Links">
                    <Form.Group controlId="formLinks" className="mb-3">
                      <Form.Label>Links partilhados</Form.Label>
                      <Form.Text className="d-block text-muted mb-2">
                        Ex.: pastas do OneDrive, Google Drive, etc. Adicione
                        uma breve descrição para esclarecer o que trata cada
                        link.
                      </Form.Text>
                      {(formData.links || []).map((link, index) => (
                        <Row key={link.id ?? `new-${index}`} className="mb-2 align-items-start">
                          <Col xs={12} md={4}>
                            <Form.Control
                              type="text"
                              placeholder="Descrição (ex.: Plantas do projeto)"
                              maxLength={255}
                              value={link.descricao || ''}
                              onChange={(e) =>
                                handleLinkChange(
                                  index,
                                  'descricao',
                                  e.target.value
                                )
                              }
                            />
                          </Col>
                          <Col xs={9} md={6}>
                            <Form.Control
                              type="url"
                              placeholder="https://..."
                              maxLength={500}
                              value={link.url}
                              onChange={(e) =>
                                handleLinkChange(index, 'url', e.target.value)
                              }
                            />
                          </Col>
                          <Col xs={3} md={2} className="text-end">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleRemoveLink(index)}
                            >
                              Remover
                            </Button>
                          </Col>
                        </Row>
                      ))}
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleAddLink}
                      >
                        Adicionar link
                      </Button>
                    </Form.Group>
                  </Tab>
                  <Tab
                    eventKey="material"
                    title={
                      <>
                        Material
                        {totalConflitos > 0 && (
                          <Badge
                            bg="warning"
                            text="dark"
                            className="ms-1"
                            title="Material já requisitado nestes dias"
                          >
                            {totalConflitos}
                          </Badge>
                        )}
                      </>
                    }
                  >
                    <TarefaRequisicoesTab
                      requisicoes={formData.requisicoes || []}
                      conflitos={requisicaoConflitos}
                      prazoEstimado={formData.prazoEstimado}
                      prazoReal={formData.prazoReal}
                      onChange={(requisicoes) =>
                        setFormData((prevData) => ({
                          ...prevData,
                          requisicoes,
                        }))
                      }
                    />
                  </Tab>
                  <Tab eventKey="subtarefas" title="Subtarefas">
                    <div className="mb-3">
                      {!isEditing ? (
                        <>
                          <p className="text-muted">
                            Pode dividir já esta tarefa em subtarefas, uma
                            para cada colaborador selecionado, assim que a
                            criar.
                          </p>
                          {formData.userIds.length >= 2 ? (
                            <>
                              <Form.Check
                                type="checkbox"
                                label="Dividir esta tarefa em subtarefas entre os colaboradores selecionados"
                                checked={divideOnCreate}
                                onChange={(e) =>
                                  setDivideOnCreate(e.target.checked)
                                }
                                className="mb-2"
                              />
                              {divideOnCreate && (
                                <div className="mb-3">
                                  {users
                                    .filter((u) =>
                                      formData.userIds.includes(u.id)
                                    )
                                    .map((u) => (
                                      <Form.Group key={u.id} className="mb-2">
                                        <Form.Label className="mb-1">
                                          {u.name}
                                        </Form.Label>
                                        <Form.Control
                                          as="textarea"
                                          rows={2}
                                          placeholder="Descrição da subtarefa (opcional)"
                                          maxLength={255}
                                          value={
                                            subtarefaDescricoes[u.id] || ''
                                          }
                                          onChange={(e) =>
                                            setSubtarefaDescricoes((prev) => ({
                                              ...prev,
                                              [u.id]: e.target.value,
                                            }))
                                          }
                                        />
                                      </Form.Group>
                                    ))}
                                </div>
                              )}
                            </>
                          ) : (
                            <Form.Text className="d-block text-muted">
                              Selecione pelo menos 2 colaboradores para poder
                              dividir a tarefa em subtarefas.
                            </Form.Text>
                          )}
                        </>
                      ) : tarefa && !isDividida ? (
                          <>
                            <p className="text-muted">
                              Divida esta tarefa em subtarefas, uma para cada
                              colaborador atribuído, com percentual igual
                              entre todos. Pode descrever, opcionalmente, em
                              que consiste a parte de cada colaborador.
                            </p>
                            {tarefa.users.length >= 2 &&
                              (hasPermission(Permission.ASSIGN_TASK) ||
                                isAdmin() ||
                                isManager()) && (
                                <div className="mb-3">
                                  {tarefa.users.map((tarefaUser) => (
                                    <Form.Group
                                      key={tarefaUser.id}
                                      className="mb-2"
                                    >
                                      <Form.Label className="mb-1">
                                        {tarefaUser.name}
                                      </Form.Label>
                                      <Form.Control
                                        as="textarea"
                                        rows={2}
                                        placeholder="Descrição da subtarefa (opcional)"
                                        maxLength={255}
                                        value={
                                          subtarefaDescricoes[
                                            tarefaUser.id
                                          ] || ''
                                        }
                                        onChange={(e) =>
                                          setSubtarefaDescricoes((prev) => ({
                                            ...prev,
                                            [tarefaUser.id]: e.target.value,
                                          }))
                                        }
                                      />
                                    </Form.Group>
                                  ))}
                                </div>
                              )}
                            {(hasPermission(Permission.ASSIGN_TASK) ||
                              isAdmin() ||
                              isManager()) && (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                disabled={tarefa.users.length < 2}
                                onClick={() =>
                                  dividir(
                                    tarefa.users.map((tarefaUser) => ({
                                      userId: tarefaUser.id,
                                      descricao:
                                        subtarefaDescricoes[tarefaUser.id]?.trim() ||
                                        undefined,
                                    }))
                                  )
                                }
                              >
                                Dividir em subtarefas entre colaboradores
                              </Button>
                            )}
                            {tarefa.users.length < 2 && (
                              <Form.Text className="d-block text-muted mt-2">
                                A tarefa precisa de pelo menos 2 colaboradores
                                atribuídos para poder ser dividida.
                              </Form.Text>
                            )}
                          </>
                        ) : tarefa ? (
                          <>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <strong>Progresso total</strong>
                              <span>{totalPercentual}%</span>
                            </div>
                            <ProgressBar
                              now={totalPercentual}
                              variant={
                                totalPercentual >= 100
                                  ? 'success'
                                  : totalPercentual >= 50
                                  ? 'warning'
                                  : 'danger'
                              }
                              className="mb-3"
                            />
                            {(hasPermission(Permission.ASSIGN_TASK) ||
                              isAdmin() ||
                              isManager()) && (
                              <div className="mb-3">
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  disabled={subtarefas.some(
                                    (s) => s.concluida,
                                  )}
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        'Tem a certeza que quer desfazer a divisão em subtarefas? Esta ação não pode ser revertida.',
                                      )
                                    ) {
                                      desfazer();
                                    }
                                  }}
                                >
                                  Desfazer divisão
                                </Button>
                                {subtarefas.some((s) => s.concluida) && (
                                  <Form.Text className="d-block text-muted mt-1">
                                    Não é possível desfazer: já existem
                                    subtarefas concluídas.
                                  </Form.Text>
                                )}
                              </div>
                            )}
                            <ul className="list-group">
                              {subtarefas.map((subtarefa) => {
                                const canEdit =
                                  user?.id === subtarefa.user.id ||
                                  hasPermission(Permission.ASSIGN_TASK) ||
                                  isAdmin() ||
                                  isManager();
                                const isEditingThis =
                                  editingSubtarefaId === subtarefa.id;
                                return (
                                  <li
                                    key={subtarefa.id}
                                    className="list-group-item"
                                  >
                                    <div className="d-flex justify-content-between align-items-center">
                                      <div className="flex-grow-1 me-2">
                                        <div>{subtarefa.user.name}</div>
                                        {isEditingThis ? (
                                          <Form.Control
                                            as="textarea"
                                            rows={2}
                                            className="mt-1"
                                            maxLength={255}
                                            value={editingDescricao}
                                            onChange={(e) =>
                                              setEditingDescricao(
                                                e.target.value,
                                              )
                                            }
                                          />
                                        ) : (
                                          <small className="text-muted">
                                            {subtarefa.percentual}%
                                            {subtarefa.descricao
                                              ? ` — ${subtarefa.descricao}`
                                              : ''}
                                          </small>
                                        )}
                                      </div>
                                      <div className="d-flex align-items-center gap-2">
                                        {isEditingThis ? (
                                          <>
                                            <Button
                                              variant="outline-primary"
                                              size="sm"
                                              onClick={async () => {
                                                await atualizar(
                                                  subtarefa.id,
                                                  editingDescricao.trim(),
                                                );
                                                setEditingSubtarefaId(null);
                                              }}
                                            >
                                              Guardar
                                            </Button>
                                            <Button
                                              variant="outline-secondary"
                                              size="sm"
                                              onClick={() =>
                                                setEditingSubtarefaId(null)
                                              }
                                            >
                                              Cancelar
                                            </Button>
                                          </>
                                        ) : (
                                          <>
                                            {canEdit && (
                                              <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => {
                                                  setEditingSubtarefaId(
                                                    subtarefa.id,
                                                  );
                                                  setEditingDescricao(
                                                    subtarefa.descricao || '',
                                                  );
                                                }}
                                              >
                                                Editar
                                              </Button>
                                            )}
                                            {subtarefa.concluida ? (
                                              <>
                                                <Badge bg="success">
                                                  Concluída
                                                </Badge>
                                                {user?.id ===
                                                  subtarefa.user.id && (
                                                  <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() =>
                                                      reabrir(subtarefa.id)
                                                    }
                                                  >
                                                    Reabrir
                                                  </Button>
                                                )}
                                              </>
                                            ) : user?.id ===
                                              subtarefa.user.id ? (
                                              <Button
                                                variant="outline-success"
                                                size="sm"
                                                onClick={() =>
                                                  concluir(subtarefa.id)
                                                }
                                              >
                                                Marcar como concluída
                                              </Button>
                                            ) : (
                                              <Badge bg="secondary">
                                                Pendente
                                              </Badge>
                                            )}
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          </>
                        ) : null}
                      </div>
                    </Tab>
                </Tabs>
              </Col>
            </Row>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        {isEditing &&
          tarefa &&
          tarefa.status === 'DONE' &&
          !tarefa.arquivadaEm &&
          hasPermission(Permission.MOVE_CARD_TO_DONE) && (
            <Button
              variant="outline-secondary"
              onClick={() => onArchive?.(tarefa.id)}
            >
              Arquivar
            </Button>
          )}
        <Button variant="secondary" onClick={onHide}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!isDeadlineValid && formData.prazoReal !== ''}
        >
          {isEditing ? 'Atualizar' : 'Criar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TarefaModal;
